import { Button } from '@mui/material'
import { useState } from 'react'
import { Fade } from 'utoniq-ui'

import {
  ItemSelectBoxFragment,
  Maybe,
  MembershipSubscriptionPlan,
} from '@/clients/utoniq-core/schema'
import { BottomSafeArea, Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FileUploadInfo } from '@/components/pages/ConsoleItemCreate/template/ItemCreateForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { ItemEditForm, ItemEditFormProps } from './ItemEditForm'

export * from './fragments'

export type State = {
  artistId: string
  item: Maybe<ItemSelectBoxFragment>
  plans: Maybe<Pick<MembershipSubscriptionPlan, 'planId' | 'title'>>[]
  currentPlans: string[]
  progress: number
  updating: boolean
}

type FixedButtonCellProps = {
  updating: boolean
  disabled: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: ItemEditFormProps['onSubmit']
  onDrop: FileUploadInfo['onDrop']
}

export const ConsoleItemEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
  onDrop,
}) => {
  const { t } = useTranslation('consoleItemEdit')
  const { routerNames, routerPages, generatePath, router } = useRouter<{
    itemId: string
  }>()

  const [isFormValid, setIsFormValid] = useState(false)

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleItemEdit} />

      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          defaultBackPage: generatePath(routerPages.ConsoleItemDetail, {
            itemId: router.query.itemId ?? '#',
          }),
          title: t('title'),
        }}
        customNavigation={
          <FixedButtonCell
            updating={state.updating || !!state.progress}
            disabled={!isFormValid}
          />
        }
      >
        {state.item && (
          <Fade>
            <ItemEditForm
              item={state.item}
              plans={state.plans}
              currentPlans={state.currentPlans}
              onSubmit={onSubmit}
              fileUploadInfo={{
                progress: state.progress,
                onDrop,
              }}
              setIsFormValid={setIsFormValid}
            />
          </Fade>
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const FixedButtonCell: Cell<FixedButtonCellProps> = ({
  updating,
  disabled,
}) => {
  const { t } = useTranslation('consoleItemEdit')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame
        sx={{
          py: 4,
          display: 'flex',
          bgcolor: 'grey.50',
          justifyContent: 'center',
        }}
      >
        <Button
          form="item-edit-form"
          type="submit"
          size="large"
          loading={updating}
          disabled={disabled}
          sx={{ width: 244 }}
        >
          {t('save')}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}
